import React, { useCallback, useEffect } from 'react'
import { Box, useTheme } from '@mui/material'

import { useStepper } from '../SurveyFeatureStepper/useStepper'
import { useSurveyFeatureStepper } from '../../useSurveyFeatureStepper'
import FreeResponseCard from '../FreeResponseCard'
import MultipleChoiceCard from '../MultipleChoiceCard'
import LinearScaleCard from '../LinearScaleCard'
import MultiSelectCard from '../MultiSelectCard'
import { type ILinearObject, SurveyItemQuestionType } from '../../../SurveyTempleteFeature/types'
import { type ISurveySection } from '../../../../models/SurveyTemplate'
import { type ISection, type OnSubmitFn } from '../SurveyFeatureStepper/types'
import { titleFormatted } from '../../../../helpers'
import { StepNavigation } from '../StepNavigation'
import { RenderSection } from '../RenderSection'
import { buildAnswerFieldName } from '../SurveyFeatureStepper/utils'

interface RenderSectionsProps {
  rawSections: ISurveySection[]
  onSubmit: OnSubmitFn
  draftKey?: string
}

export const RenderSections = (props: RenderSectionsProps): JSX.Element => {
  const theme = useTheme()

  const { sections, renderedSectionsRef, invalidFieldNames, handleChange, handleBack, handleNext, shouldRenderSection, triggerPulse } = useSurveyFeatureStepper(props.rawSections, props.onSubmit, props.draftKey)

  const { step } = useStepper()

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [step])

  const currentRootSection = sections[step]

  const renderQuestion = useCallback(({ prompt, itemId, itemType, object, isRequired }: any, questionNumber: number, section: ISection): JSX.Element => {
    // CR-213: name is keyed by the modded section id (already on section.surveySectionId),
    // so the same itemId reused across sections resolves to a distinct field per occurrence.
    const name = buildAnswerFieldName(itemId, section.surveySectionId)
    const commonProps = {
      questionNumber,
      disabled: false,
      title: prompt,
      name,
      isRequired,
      invalidQuestionItemId: invalidFieldNames.includes(name) ? name : '',
      itemId
    }

    switch (itemType) {
      case SurveyItemQuestionType.FREE_RESPONSE:
        return <FreeResponseCard {...commonProps} onChange={(e) => { handleChange(e.target.value, section.surveySectionId, itemId, name) }} />
      case SurveyItemQuestionType.MULTIPLE_CHOICE:
        return <MultipleChoiceCard {...commonProps} onChange={(value: unknown) => { handleChange(value as string | string[], section.surveySectionId, itemId, name) }} options={object as string[]} />
      case SurveyItemQuestionType.LINEAR_SCALE:
        return <LinearScaleCard {...commonProps} onChange={(e: unknown) => { handleChange(e as string | string[], section.surveySectionId, itemId, name) }} options={object as ILinearObject[]} />
      case SurveyItemQuestionType.DESCRIPTIVE_TEXT:
        return titleFormatted(prompt)
      case SurveyItemQuestionType.MULTI_SELECT:
        return <MultiSelectCard {...commonProps} onChange={(e: unknown) => { handleChange(e as string | string[], section.surveySectionId, itemId, name) }} options={object as string[]} />
      default:
        return <></>
    }
  }, [invalidFieldNames])

  const renderQuestions = useCallback((section: ISection): JSX.Element[] => {
    let questionNumber = 0
    return section.surveyItems.map((question): JSX.Element => {
      if (question.itemType !== SurveyItemQuestionType.DESCRIPTIVE_TEXT as string) {
        questionNumber++
      }

      return (
        <Box key={question.itemId} marginTop={theme.spacing(6)}>
          {renderQuestion(question, questionNumber, section)}
        </Box>
      )
    })
  }, [renderQuestion])

  return (
    <>
      {
        currentRootSection &&
        <RenderSection root={currentRootSection} section={currentRootSection} renderedSectionsRef={renderedSectionsRef} renderQuestions={renderQuestions} shouldRenderSection={shouldRenderSection} isRoot triggerPulse={triggerPulse} />

      }
      <StepNavigation sections={sections} handleBack={handleBack} handleNext={handleNext} />
    </>
  )
}
